import React, { useState, useEffect } from 'react';
import APIService from '../services/api';

/**
 * Nationalités à compléter, carte par carte (04.11).
 *
 * Les candidats déposés avant le choix de nationalité au téléversement de la
 * pièce d'identité n'en ont pas. À l'ouverture de chaque carte, le serveur LIT
 * la pièce d'identité (GET /ao/:id/nationalites?candidate_id=…&pieces=1 : OCR du serveur, sans IA ; bande
 * MRZ vérifiée par ses sommes de contrôle), sinon ce que le CV écrit — jamais
 * une déduction du nom ou de la ville. La carte suivante se lit en avance. La personne confirme, corrige ou passe : rien n'est écrit sans
 * son clic, et l'écriture est relue avant d'être annoncée.
 */

const BASE = 'https://besoins-tat-production.up.railway.app';
const BI = ({ icon, style }) => <i className={`bi bi-${icon}`} style={style} />;
const authH = () => ({ Authorization: `Bearer ${APIService.getToken()}` });
const jsonH = () => ({ ...authH(), 'Content-Type': 'application/json' });

const btn = {
  display: 'inline-flex', alignItems: 'center', gap: '7px', padding: '9px 14px', borderRadius: '9px',
  border: '1px solid var(--input-border)', background: 'var(--ghost-bg)', color: 'var(--text)',
  fontFamily: 'inherit', fontSize: '13px', fontWeight: 600, cursor: 'pointer',
};
const btnFort = { ...btn, border: 'none', background: 'var(--accent)', color: 'var(--accent-fg)' };
const inp = {
  width: '100%', boxSizing: 'border-box', padding: '9px 11px', borderRadius: '8px',
  border: '1px solid var(--input-border)', background: 'var(--input-bg, var(--surface))',
  color: 'var(--text-strong)', fontFamily: 'inherit', fontSize: '13px', outline: 'none',
};
const lbl = { display: 'block', fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-3)', marginBottom: '5px' };
const RAPIDES = ['Suisse', 'Française', 'Italienne', 'Portugaise', 'Espagnole', 'Allemande', 'Belge', 'Tunisienne', 'Marocaine', 'Algérienne'];

const sansAccent = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toLowerCase();

/** Charge la liste ; `null` = chargement, `{ absent }` = route pas encore servie. */
export function useNationalitesManquantes(aoId, version = 0) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let annule = false;
    setData(null);
    (async () => {
      try {
        const r = await fetch(`${BASE}/ao/${aoId}/nationalites`, { headers: authH() });
        if (r.status === 404 || r.status === 501) { if (!annule) setData({ absent: true, candidats: [] }); return; }
        const j = await r.json().catch(() => ({}));
        if (!r.ok) { if (!annule) setData({ erreur: j.error || `HTTP ${r.status}`, candidats: [] }); return; }
        if (!annule) setData({ candidats: j.candidats || [] });
      } catch (e) { if (!annule) setData({ erreur: e.message, candidats: [] }); }
    })();
    return () => { annule = true; };
  }, [aoId, version]);
  return data;
}

export default function AoNationalites({ aoId, candidats, showNotification, onFini }) {
  const [idx, setIdx] = useState(0);
  const [saisie, setSaisie] = useState('');
  const [busy, setBusy] = useState(false);
  const [resultat, setResultat] = useState({});   // candidate_id → 'ok' | 'passe'
  const [erreur, setErreur] = useState(null);
  // candidate_id → la carte relue avec sa pièce ('chargement' pendant la lecture).
  const [lues, setLues] = useState({});
  const enCours = React.useRef({});
  const total = candidats.length;
  const fini = idx >= total;
  const base = candidats[idx];
  const lue = base ? lues[base.candidate_id] : null;
  const c = lue && lue !== 'chargement' ? lue : base;
  const lecture = lue === 'chargement';

  const lireCarte = React.useCallback(async (cand) => {
    if (!cand || enCours.current[cand.candidate_id]) return;
    enCours.current[cand.candidate_id] = true;
    setLues(x => ({ ...x, [cand.candidate_id]: 'chargement' }));
    let carte = cand;
    try {
      const r = await fetch(`${BASE}/ao/${aoId}/nationalites?candidate_id=${cand.candidate_id}&pieces=1`, { headers: authH() });
      const j = await r.json().catch(() => ({}));
      if (r.ok && j.candidats?.[0]) carte = { ...cand, ...j.candidats[0] };
    } catch (e) { /* la carte garde ce que la liste savait */ }
    setLues(x => ({ ...x, [cand.candidate_id]: carte }));
  }, [aoId]);

  useEffect(() => {
    if (fini) return;
    lireCarte(candidats[idx]);
    lireCarte(candidats[idx + 1]);
  }, [idx, fini, candidats, lireCarte]);

  useEffect(() => {
    setErreur(null);
    if (!lecture) setSaisie(c?.proposition?.valeur || '');
  }, [idx, c?.candidate_id, lecture, c?.proposition?.valeur]); // eslint-disable-line react-hooks/exhaustive-deps

  const confirmer = async () => {
    const v = saisie.trim();
    if (!c || !v) return;
    setBusy(true); setErreur(null);
    try {
      const r = await fetch(`${BASE}/candidats/vue/${c.candidate_id}`, {
        method: 'PUT', headers: jsonH(), body: JSON.stringify({ nationalite: v }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      // Preuve : la fiche relue porte bien la nationalité.
      const rl = await fetch(`${BASE}/candidats/vue/${c.candidate_id}`, { headers: authH() });
      const jl = await rl.json().catch(() => ({}));
      const f = jl.data || jl.candidat || jl;
      const lu = f?.nationalite ?? f?.nationality;
      if (sansAccent(lu) !== sansAccent(v)) throw new Error("la fiche relue ne porte pas la nationalité — rien n'a été conservé");
      setResultat(x => ({ ...x, [c.candidate_id]: 'ok' }));
      setIdx(i => i + 1);
    } catch (e) {
      setErreur(`Enregistrement impossible : ${e.message}`);
      showNotification?.(`Nationalité non enregistrée (${c.prenom || ''} ${c.nom || ''}) : ${e.message}`, 'error');
    } finally { setBusy(false); }
  };

  const passer = () => { if (c) setResultat(x => ({ ...x, [c.candidate_id]: x[c.candidate_id] || 'passe' })); setIdx(i => i + 1); };
  const nb = (v) => Object.values(resultat).filter(x => x === v).length;

  const entete = (
    <>
      <div style={{ fontSize: '12px', color: 'var(--text-3)', marginBottom: '10px' }}>
        {fini ? 'Récapitulatif des nationalités.' : `Carte ${idx + 1} sur ${total} — la nationalité est écrite sur la fiche au clic.`}
      </div>
      <div style={{ height: '4px', borderRadius: '2px', background: 'var(--code-bg)', marginBottom: '16px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${total ? (Math.min(idx, total) / total) * 100 : 100}%`, background: 'var(--accent)', transition: 'width .2s' }} />
      </div>
    </>
  );

  if (fini) {
    return (
      <>
        {entete}
        <div style={{ fontSize: '13px', color: 'var(--text-2)', lineHeight: 1.7 }}>
          {nb('ok')} nationalité{nb('ok') > 1 ? 's' : ''} enregistrée{nb('ok') > 1 ? 's' : ''}
          {nb('passe') > 0 && <> · {nb('passe')} passée{nb('passe') > 1 ? 's' : ''} (à compléter plus tard)</>}
          {' '}sur {total}.
        </div>
        <div style={{ marginTop: '18px', display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          {total > 0 && <button onClick={() => setIdx(0)} style={btn}>Revoir les cartes</button>}
          <button onClick={() => onFini?.(nb('ok'))} style={btnFort}><BI icon="arrow-left" /> Revenir aux contrats</button>
        </div>
      </>
    );
  }

  const prop = c.proposition;
  const deja = resultat[c.candidate_id] === 'ok';
  return (
    <>
      {entete}
      <div style={{ border: '1px solid var(--border)', borderRadius: '12px', padding: '16px 18px' }}>
        <div style={{ fontSize: '16px', fontWeight: 700, color: 'var(--text-strong)' }}>
          {[c.prenom, c.nom].filter(Boolean).join(' ') || `Candidat ${c.candidate_id}`}
          {deja && <span style={{ marginLeft: '8px', fontSize: '11.5px', fontWeight: 600, color: 'var(--erp-success)' }}><BI icon="check2" /> enregistrée</span>}
        </div>
        {(c.lots || []).length > 0 && (
          <div style={{ fontSize: '12px', color: 'var(--text-3)', marginTop: '2px' }}>{c.lots.join(' · ')}</div>
        )}

        <div style={{ marginTop: '14px', padding: '10px 12px', borderRadius: '9px', background: 'var(--code-bg)' }}>
          {lecture ? (
            <div style={{ fontSize: '12.5px', color: 'var(--text-2)' }}>
              <BI icon="arrow-repeat" style={{ marginRight: '6px', animation: 'aoi-spin .8s linear infinite', display: 'inline-block' }} />
              Lecture de la pièce d'identité…
            </div>
          ) : prop ? (
            <>
              <div style={{ fontSize: '12.5px', fontWeight: 600, color: 'var(--text-strong)' }}>
                <BI icon={prop.source === 'piece' ? 'person-vcard' : prop.source === 'cv' ? 'file-earmark-text' : 'info-circle'} style={{ marginRight: '6px' }} />
                {prop.source === 'piece'
                  ? (prop.certain ? `Lue sur la pièce d'identité : ${prop.valeur}` : `Lue sur la pièce d'identité : ${prop.valeur} — à vérifier`)
                  : prop.source === 'cv' ? `Lue dans le CV : ${prop.valeur}` : `Proposée par Leman : ${prop.valeur} — à vérifier`}
              </div>
              {prop.source !== 'piece' && c.piece?.raison && (
                <div style={{ marginTop: '4px', fontSize: '11.5px', color: 'var(--text-3)' }}>Pièce d'identité : {c.piece.raison}</div>
              )}
              {prop.extrait && (
                <div style={{ marginTop: '5px', fontSize: '12px', color: 'var(--text-2)', fontStyle: 'italic', lineHeight: 1.5 }}>« {prop.extrait} »</div>
              )}
            </>
          ) : (
            <div style={{ fontSize: '12.5px', color: 'var(--text-2)' }}>
              <BI icon="question-circle" style={{ marginRight: '6px' }} />
              Pas de proposition : {c.raison || 'aucune nationalité écrite dans ses CV'}. À saisir si vous la connaissez, sinon passez.
            </div>
          )}
        </div>

        <div style={{ marginTop: '14px' }}>
          <label style={lbl} htmlFor="aonat-saisie">Nationalité</label>
          <input id="aonat-saisie" type="text" value={saisie} placeholder="Française, Suisse, Française / Suisse…" autoFocus
            onChange={e => setSaisie(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && saisie.trim() && !busy) confirmer(); }} style={inp} />
          <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {RAPIDES.map(n => (
              <button key={n} type="button" onClick={() => setSaisie(n)}
                style={{ ...btn, padding: '5px 10px', fontSize: '12px', borderRadius: '6px',
                  ...(sansAccent(saisie) === sansAccent(n) ? { background: 'var(--accent)', color: 'var(--accent-fg)', border: 'none' } : {}) }}>{n}</button>
            ))}
          </div>
          <div style={{ marginTop: '8px', fontSize: '11.5px', color: 'var(--text-3)' }}>
            Suisse (même double nationalité) : aucun permis demandé. Autre nationalité : le permis par défaut de l'organisation s'applique si la fiche n'en a pas.
          </div>
        </div>
        {erreur && <div role="alert" style={{ marginTop: '10px', fontSize: '12px', color: 'var(--danger-fg)' }}>{erreur}</div>}
      </div>

      <div style={{ marginTop: '18px', display: 'flex', justifyContent: 'flex-end', gap: '8px', flexWrap: 'wrap' }}>
        <button onClick={() => onFini?.(nb('ok'))} disabled={busy} style={{ ...btn, marginRight: 'auto' }}>Revenir aux contrats</button>
        {idx > 0 && <button onClick={() => setIdx(idx - 1)} disabled={busy} style={btn}>Précédent</button>}
        <button onClick={passer} disabled={busy} style={btn}>Passer</button>
        <button onClick={confirmer} disabled={busy || lecture || !saisie.trim()} style={{ ...btnFort, opacity: busy || lecture || !saisie.trim() ? 0.6 : 1 }}>
          <BI icon={busy ? 'arrow-repeat' : 'check2'} style={busy ? { animation: 'aoi-spin .8s linear infinite' } : undefined} />
          {busy ? 'Enregistrement…' : idx + 1 < total ? 'Confirmer et suivant' : 'Confirmer et terminer'}
        </button>
      </div>
    </>
  );
}
